import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { useProjects } from '../api/hooks'
import { formatEUR2, formatPct } from '../format'
import { statusLabels, type ProjectStatus } from '../api/types'
import { useAmountsHidden } from '../privacy'
import PrivacyToggle from '../components/PrivacyToggle'
import StatCard from '../components/StatCard'

const statusColors: Record<ProjectStatus, string> = {
  draft: '#E0A96D',
  active: '#C97B4A',
  on_hold: '#D8B8A8',
  done: '#7FBF9E',
  abandoned: '#D36C6C',
}

export default function DashboardPage() {
  const projects = useProjects()
  const projList = projects.data ?? []
  useAmountsHidden()

  const rentalProjects = projList.filter((p) => p.simulation && p.simulation.monthlyRent > 0)
  const avgYield =
    rentalProjects.length > 0
      ? rentalProjects.reduce((s, p) => s + (p.simulation?.results.netYield ?? 0), 0) /
        rentalProjects.length
      : 0
  const cumulatedCashFlow = rentalProjects.reduce(
    (s, p) => s + (p.simulation?.results.monthlyCashFlow ?? 0),
    0,
  )

  const statusCounts = projList.reduce(
    (acc, p) => {
      acc[p.status] = (acc[p.status] ?? 0) + 1
      return acc
    },
    {} as Partial<Record<ProjectStatus, number>>,
  )
  const statusOrder = Object.keys(statusLabels) as ProjectStatus[]
  const donutSegments = statusOrder
    .map((status) => ({ status, count: statusCounts[status] ?? 0 }))
    .filter((s) => s.count > 0)

  let cursor = 0
  const gradientStops = donutSegments
    .map((seg) => {
      const start = (cursor / projList.length) * 360
      cursor += seg.count
      const end = (cursor / projList.length) * 360
      return `${statusColors[seg.status]} ${start}deg ${end}deg`
    })
    .join(', ')

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          mb: 3,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h4">Tableau de bord</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {projList.length} projet{projList.length > 1 ? 's' : ''} actif
            {projList.length > 1 ? 's' : ''} · {formatEUR2(cumulatedCashFlow)}/mois de cash-flow
            cumulé
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <PrivacyToggle />
          <Button
            component={RouterLink}
            to="/simulations/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Nouvelle simulation
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          mb: 4,
        }}
      >
        <StatCard label="Patrimoine simulé" value={`${projList.length} projets`} />
        <StatCard label="Rendement net moyen" value={formatPct(avgYield)} />
        <StatCard
          label="Cash-flow cumulé"
          value={`${formatEUR2(cumulatedCashFlow)}/mois`}
          tone={cumulatedCashFlow >= 0 ? 'positive' : 'negative'}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Statut des projets
            </Typography>
            <Box
              sx={{
                width: 160,
                height: 160,
                borderRadius: '50%',
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background:
                  projList.length > 0
                    ? `conic-gradient(${gradientStops})`
                    : 'rgba(255,255,255,0.06)',
              }}
            >
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h4">{projList.length}</Typography>
              </Box>
            </Box>
            <Stack spacing={1} sx={{ mt: 3 }}>
              {donutSegments.map((seg) => (
                <Stack key={seg.status} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: statusColors[seg.status],
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {statusLabels[seg.status]} · {seg.count}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Projets récents
            </Typography>
            {projList.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Aucun projet. Créez-en un depuis une simulation.
              </Typography>
            )}
            <Stack spacing={1.5}>
              {projList.slice(0, 5).map((p) => {
                const r = p.simulation?.results
                return (
                  <Box
                    key={p.id}
                    component={RouterLink}
                    to={`/projects/${p.id}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      textDecoration: 'none',
                      color: 'inherit',
                      p: 1.5,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'rgba(245,245,245,0.06)' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        flexShrink: 0,
                        bgcolor: 'action.hover',
                        border: '1px dashed',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AddPhotoAlternateOutlinedIcon sx={{ color: 'text.secondary' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600 }}>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                        {p.simulation?.address || '—'}
                      </Typography>
                    </Box>
                    <Stack spacing={0.25} sx={{ flexShrink: 0, alignItems: 'flex-end' }}>
                      {r && (
                        <>
                          {!!p.simulation?.monthlyRent && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: r.monthlyCashFlow >= 0 ? 'success.main' : 'error.main',
                              }}
                            >
                              Loc. {formatEUR2(r.monthlyCashFlow)}/mois
                            </Typography>
                          )}
                          {!!p.simulation?.pricePerM2Resale && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  r.netMarginChargePercent >= 0 ? 'success.main' : 'error.main',
                              }}
                            >
                              Revente {formatPct(r.netMarginChargePercent)}
                            </Typography>
                          )}
                        </>
                      )}
                      <Chip
                        size="small"
                        label={statusLabels[p.status]}
                        sx={{
                          mt: 0.5,
                          bgcolor: statusColors[p.status],
                          color: 'background.default',
                        }}
                      />
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
